// Formatting methods for TheoryCoach
// Extracted to reduce main file size and prevent Cursor crashes

/**
 * Format AI response to be short and actionable (SHORT messages)
 */
export const formatResponse = (response: string, _language: string): string => {
  // Remove any emojis first
  let formatted = response.replace(/[\u{1F300}-\u{1F9FF}]/gu, ''); // Remove emoji range
  formatted = formatted.replace(/[\u{2600}-\u{26FF}]/gu, ''); // Remove misc symbols
  formatted = formatted.replace(/[\u{2700}-\u{27BF}]/gu, ''); // Remove dingbats
  formatted = formatted.replace(/[✅❌⚠️💡🎉😊👍👎]/g, ''); // Remove common emojis
  
  // Remove any "weak" references
  formatted = formatted.replace(/\bweak\b/gi, 'focus area');
  formatted = formatted.replace(/\bweakness\b/gi, 'area to practice');
  
  // Remove guarantees
  formatted = formatted.replace(/\b(guaranteed|will pass|you will pass)\b/gi, 'keep practicing');
  
  // Limit to 1 sentence preferred, max 2 sentences if necessary
  const sentenceMatches = formatted.match(/[^.!?؟]+[.!?؟]*/g)?.map(s => s.trim()).filter(Boolean) || [];
  if (sentenceMatches.length > 1) {
    // Prefer 1 sentence, but allow 2 if the first sentence is very short (< 10 words)
    const firstSentenceWords = sentenceMatches[0].split(/\s+/).filter(Boolean).length;
    if (firstSentenceWords >= 10 || sentenceMatches.length > 2) {
      // First sentence is long enough or there are more than 2 sentences - use only first
      formatted = sentenceMatches[0];
    } else {
      // First sentence is short - allow 2 sentences
      formatted = sentenceMatches.slice(0, 2).join(' ');
    }
  }

  // Keep it VERY SHORT - max 20 words (prefer ≤15 words)
  const maxWords = 20;
  const words = formatted.split(/\s+/).filter(Boolean);
  if (words.length > maxWords) {
    formatted = words.slice(0, maxWords).join(' ').replace(/[.!?؟]$/, '');
    formatted = `${formatted}…`;
  }

  // Ensure we don't end on a dangling connector
  formatted = formatted.replace(/\s+(and|or|but|because|so|therefore|omdat|maar|dus|لأن|لكن|فقط)$/i, '').trim();
  
  return formatted.trim();
};

export const formatCoachReply = (text: string, language: string): string => {
  return formatResponse(text || '', language);
};

export const getInputPlaceholder = (language: string): string => {
  if (language === 'nl') return 'Ask TheoryCoach (3 questions left for this question)';
  if (language === 'ar') return 'Ask TheoryCoach (3 questions left for this question)';
  return 'Ask TheoryCoach (3 questions left for this question)';
};

export const getDisclaimer = (language: string): string => {
  if (language === 'nl') return 'TheoryCoach kan fouten maken. Controleer belangrijke info.';
  if (language === 'ar') return 'قد يخطئ TheoryCoach. تحقّق من المعلومات المهمة.';
  return 'TheoryCoach can make mistakes. Check important info.';
};

export const getNextStepText = (language: string): string => {
  if (language === 'nl') return 'Volgende stap: oefen nog 2 vragen.';
  if (language === 'ar') return 'الخطوة التالية: تدرب على سؤالين إضافيين.';
  return 'Next step: practice 2 more questions.';
};

export const getWhyWrongText = (params: {
  language: string;
  selectedLetter?: string;
  correctLetter?: string;
  correctText?: string;
}): string => {
  const { language, selectedLetter, correctLetter, correctText } = params;
  if (language === 'nl') {
    return `Het juiste antwoord is ${correctLetter ? `${correctLetter} - ` : ''}${correctText || ''}. ${
      selectedLetter ? `Optie ${selectedLetter} past niet bij de regel.` : 'Deze optie past niet bij de regel.'
    }`;
  }
  if (language === 'ar') {
    return `الإجابة الصحيحة هي ${correctLetter ? `${correctLetter} - ` : ''}${correctText || ''}. ${
      selectedLetter ? `الخيار ${selectedLetter} لا يتوافق مع القاعدة.` : 'هذا الخيار لا يتوافق مع القاعدة.'
    }`;
  }
  return `The correct answer is ${correctLetter ? `${correctLetter} - ` : ''}${correctText || ''}. ${
    selectedLetter ? `Option ${selectedLetter} doesn't match the rule.` : "This option doesn't match the rule."
  }`;
};

export const getAnswerFirstLines = (params: {
  language: string;
  correctLetter?: string;
  correctText?: string;
  wrongLetter?: string;
  wrongText?: string;
  ruleSnippet?: string;
}): { correctLine: string; wrongLine: string } => {
  const { language, correctLetter, correctText, wrongLetter, wrongText, ruleSnippet } = params;
  const correctLabel = `${correctLetter ? `${correctLetter} - ` : ''}${correctText || ''}`.trim();
  const _wrongLabel = `${wrongLetter ? `${wrongLetter} - ` : ''}${wrongText || ''}`.trim();
  const trimmedRule = ruleSnippet && ruleSnippet.length <= 80 ? ruleSnippet : '';
  const rulePart = trimmedRule ? ` ${trimmedRule}` : '';

  if (language === 'nl') {
    return {
      correctLine: `Correct. ${correctLabel} volgt de regel.${rulePart}`,
      wrongLine: `Het juiste antwoord is ${correctLabel}.`
    };
  }
  if (language === 'ar') {
    return {
      correctLine: `صحيح. ${correctLabel} يتبع القاعدة.${rulePart}`,
      wrongLine: `الإجابة الصحيحة هي ${correctLabel}.`
    };
  }
  return {
    correctLine: `Correct. ${correctLabel} follows the rule.${rulePart}`,
    wrongLine: `The correct answer is ${correctLabel}.`
  };
};

export const getConciseExplanationText = (explanation: string): string => {
  if (!explanation) return '';
  const clean = explanation.replace(/\s+/g, ' ').trim();
  // Get first sentence - FULL sentence, no truncation for key rules
  const firstSentence = clean.match(/[^.!?؟]+[.!?؟]*/)?.[0]?.trim() || clean;
  // Return full first sentence - user needs to see the complete key rule
  return firstSentence;
};

export const formatQuestionForVoice = (questionText: string, options?: Array<{ text: string }>, language?: string): string => {
  let fullText = questionText || '';
  if (options && options.length > 0) {
    const letters = ['A', 'B', 'C', 'D'];
    const optionsText = options
      .slice(0, 4)
      .map((opt, idx) => `${letters[idx]}: ${opt.text}`)
      .join('. ');
    if (optionsText) {
      fullText += ` ${language === 'nl' ? 'Opties' : language === 'ar' ? 'الخيارات' : 'Options'}: ${optionsText}.`;
    }
  }
  return fullText.trim();
};

export const formatExplanationForVoice = (params: {
  language: string;
  isCorrect: boolean;
  explanation: string;
  selectedLetter?: string;
  correctLetter?: string;
  correctText?: string;
}): string => {
  const concise = getConciseExplanationText(params.explanation);
  if (params.isCorrect) return concise;
  return `${getWhyWrongText(params)} ${concise}`.trim();
};
